/**
 * Was ein Klick auf eine Quelle tun soll: **im Browser öffnen** oder herunterladen.
 *
 * Die Quellenzeile hing an `DownloadLink` mit `saveToFileSystem` — das ist per Konstruktion ein
 * Download samt „Speichern unter"-Dialog. Für ein PDF ist das der Umweg für etwas, das der Browser
 * selbst kann, und genau der Umweg, den `DocumentViewerDialog` und die Dokumentmenüs im Chat schon
 * überall sonst vermeiden.
 *
 * Nachschlagen heißt außerdem: an der **Fundstelle** nachschlagen. Ein PDF-Betrachter versteht
 * `#page=N`, also wird die gefundene Seite mit geöffnet — bei einem 31-seitigen Mietvertrag ist das
 * der Unterschied zwischen „hier steht es" und „irgendwo hier drin steht es".
 */

/** Die Endung, ohne Query und Fragment — die URL trägt beides (`?pages=`, `#page=`). */
export const fileExtensionOf = (url: string): string => {
  const withoutQuery = String(url || '').split(/[?#]/)[0];
  const last = withoutQuery.split('/').pop() || '';
  const dot = last.lastIndexOf('.');
  return dot > 0 ? last.slice(dot + 1).toLowerCase() : '';
};

/**
 * Was ein Browser selbst darstellt. Bewusst eine **Positivliste**: bei allem anderen — docx, xlsx,
 * csv, zip — liefert ein neuer Tab nur einen Download ohne Dateinamen oder eine leere Seite, und
 * dann ist der Download die ehrlichere Antwort.
 */
const BROWSER_RENDERED = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'txt',
  'md',
  'html',
  'htm',
]);

/**
 * Eine **Webseite**: eine absolute http(s)-Adresse, deren letzter Pfadteil keine Datei ist — der
 * Verweis auf ein ganzes Regelwerk (`https://www.gesetze-im-internet.de/arbst_ttv_2004/`) oder
 * seinen Herausgeber (`https://www.vdi.de/richtlinien`). Solche Quellen liefen in `DownloadLink`:
 * Ohne Endung wurde aus dem leeren letzten Pfadteil der Name `download` und aus der fehlenden Endung
 * `.bin` — der Speichern-Dialog bot eine Webseite als `download.bin` an (30.09.2026).
 */
const WEB_PAGE_EXTENSIONS = new Set(['', 'html', 'htm', 'php', 'asp', 'aspx', 'jsp']);
export const isWebPage = (url: string): boolean => {
  const target = String(url || '');
  if (!/^https?:\/\//i.test(target) || /\/documents\//.test(target)) return false;
  
  // Extract path from URL (everything after the domain)
  const withoutQuery = target.split(/[?#]/)[0];
  const domainEnd = withoutQuery.indexOf('/', 8); // Skip 'https://' (8 chars minimum)
  const path = domainEnd >= 0 ? withoutQuery.slice(domainEnd) : '';
  
  // If there's no path or just '/', treat as a web page (domain-only URLs like https://www.vdi.de)
  if (path === '' || path === '/') return true;
  
  return WEB_PAGE_EXTENSIONS.has(fileExtensionOf(target));
};

export const opensInBrowser = (url: string): boolean => BROWSER_RENDERED.has(fileExtensionOf(url)) || isWebPage(url);

/** Dieselbe URL, bei einem PDF um die Fundstelle ergänzt. */
export const browserOpenUrl = (url: string, page?: number): string => {
  const target = String(url || '');
  if (!target) return '';
  const start = Number(page);
  if (fileExtensionOf(target) !== 'pdf' || !Number.isFinite(start) || start < 1) return target;
  // Ein vorhandenes Fragment wird ersetzt, nicht angehängt — zwei `#` ergäben eine tote URL.
  return `${target.split('#')[0]}#page=${Math.floor(start)}`;
};
